import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { loadListViewState, saveListViewState } from '../utils/listViewState';

/**
 * Состояние списка с записью в sessionStorage.
 * При возврате «Назад» в браузере состояние читается заново из storage.
 */
export function usePersistedListView(storageKey) {
  const location = useLocation();
  const scrollRestoredRef = useRef(false);
  const initial = useRef(null);
  if (initial.current === null) {
    initial.current = loadListViewState(storageKey);
  }

  const [page, setPage] = useState(initial.current.page);
  const [rowsPerPage, setRowsPerPage] = useState(initial.current.rowsPerPage);
  const [searchInput, setSearchInput] = useState(initial.current.searchInput);
  const [search, setSearch] = useState(initial.current.search);
  const [collectionFilter, setCollectionFilter] = useState(initial.current.collectionFilter);
  const [statusFilter, setStatusFilter] = useState(initial.current.statusFilter);
  const [viewMode, setViewMode] = useState(initial.current.viewMode);

  const getSnapshot = useCallback(
    () => ({
      page,
      rowsPerPage,
      viewMode,
      search,
      searchInput,
      collectionFilter,
      statusFilter,
    }),
    [page, rowsPerPage, viewMode, search, searchInput, collectionFilter, statusFilter]
  );

  // Синхронная запись до paint — надёжнее useEffect при быстром уходе со страницы
  useLayoutEffect(() => {
    saveListViewState(storageKey, getSnapshot());
  }, [storageKey, getSnapshot]);

  // При каждом появлении страницы в истории (в т.ч. «Назад») — заново из storage
  useEffect(() => {
    const stored = loadListViewState(storageKey);
    setPage(stored.page);
    setRowsPerPage(stored.rowsPerPage);
    setSearch(stored.search);
    setSearchInput(stored.searchInput || stored.search);
    setCollectionFilter(stored.collectionFilter);
    setStatusFilter(stored.statusFilter);
    setViewMode(stored.viewMode);
    scrollRestoredRef.current = false;
  }, [storageKey, location.key]);

  const persistBeforeLeave = useCallback(() => {
    saveListViewState(storageKey, {
      ...getSnapshot(),
      scrollY: window.scrollY || document.documentElement.scrollTop || 0,
    });
  }, [storageKey, getSnapshot]);

  const restoreScroll = useCallback(
    (isLoading) => {
      if (isLoading || scrollRestoredRef.current) return;
      scrollRestoredRef.current = true;
      const y = loadListViewState(storageKey).scrollY;
      if (y > 0) {
        requestAnimationFrame(() => {
          window.scrollTo(0, y);
          requestAnimationFrame(() => window.scrollTo(0, y));
        });
      }
    },
    [storageKey]
  );

  const handleSearchClick = () => {
    const next = {
      ...getSnapshot(),
      search: searchInput,
      searchInput,
      page: 0,
    };
    saveListViewState(storageKey, next);
    setSearch(searchInput);
    setPage(0);
  };

  return {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    searchInput,
    setSearchInput,
    search,
    setSearch,
    collectionFilter,
    setCollectionFilter,
    statusFilter,
    setStatusFilter,
    viewMode,
    setViewMode,
    persistBeforeLeave,
    restoreScroll,
    handleSearchClick,
  };
}
